
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { LogOut } from 'lucide-react';
import { DashboardSidebar } from '@/components/DashboardSidebar';
import { ChatInterface } from '@/components/ChatInterface';
import { BottomNavigation } from '@/components/BottomNavigation';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProfileSection } from '@/components/dashboard/ProfileSection';
import { AnnouncementsSection } from '@/components/dashboard/AnnouncementsSection';
import { ListingsSection } from '@/components/dashboard/ListingsSection';
import { SettingsSection } from '@/components/dashboard/SettingsSection';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

const BreederDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('profile');
  const [signOutLoading, setSignOutLoading] = useState(false);
  const { signOut, subscription, loading: authLoading, profile, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle URL parameters for section navigation
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['profile', 'messages', 'settings'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Handle successful checkout - refresh subscription and show toast
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true' && refreshSubscription) {
      refreshSubscription().then(() => {
        toast({
          title: "Subscription Activated",
          description: "Your subscription has been successfully activated!",
        });
        // Clean up URL parameters
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('success');
          return newParams;
        });
      }).catch((error) => {
        console.error('Failed to refresh subscription after checkout:', error);
        toast({
          title: "Subscription Update",
          description: "Your payment was successful. Please refresh the page if your subscription status doesn't update shortly.",
        });
      });
    }
  }, [searchParams, refreshSubscription, toast, setSearchParams]);

  // Check if user has active subscription or freemium tier
  const hasActiveAccess = subscription?.subscribed || 
                         profile?.subscription_active || 
                         (profile?.admin_tier && profile.admin_tier === 'freemium');

  const breeder = {
    id: 1,
    name: "Johnson Ranch",
    profileImage: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face",
    logo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop",
    location: "Texas, USA",
    description: "Johnson Ranch has been breeding premium Angus cattle for over three decades.",
    subscriptionActive: hasActiveAccess
  };

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleActivateSubscription = () => {
    navigate('/pricing');
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <ErrorBoundary>
            <div className="w-full max-w-none space-y-8">
              <ProfileSection 
                breeder={breeder} 
                authLoading={authLoading}
              />
              <AnnouncementsSection 
                subscriptionActive={breeder.subscriptionActive}
                authLoading={authLoading}
              />
              <ListingsSection 
                subscriptionActive={breeder.subscriptionActive}
                authLoading={authLoading}
              />
            </div>
          </ErrorBoundary>
        );

      case 'messages':
        return (
          <ErrorBoundary>
            <div className="w-full max-w-none">
              <ChatInterface 
                onBack={() => setActiveSection('profile')}
              />
            </div>
          </ErrorBoundary>
        );

      case 'settings':
        return (
          <ErrorBoundary>
            <div className="w-full max-w-none">
              <SettingsSection subscription={subscription} />
            </div>
          </ErrorBoundary>
        );

      default:
        return null;
    }
  };

  return (
    <ProtectedRoute requiredUserType="breeder">
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-gray-50">
          <DashboardSidebar 
            userType="breeder"
            userId={breeder.id}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            userName={breeder.name}
            subscriptionActive={breeder.subscriptionActive}
          />
          
          <SidebarInset className="flex-1">
            <div className="w-full px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Your Dashboard</h1>
                </div>
                <div className="flex items-center gap-4">
                  {!authLoading && subscription !== null && !hasActiveAccess && (
                    <Button
                      onClick={handleActivateSubscription}
                      className="bg-green-700 hover:bg-green-800 text-white font-medium px-6 py-2"
                    >
                      ACTIVATE SUBSCRIPTION
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleSignOut}
                    disabled={signOutLoading}
                    className="flex items-center gap-2"
                  >
                    {signOutLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      <LogOut className="w-4 h-4" />
                    )}
                    {signOutLoading ? 'Signing Out...' : 'Sign Out'}
                  </Button>
                </div>
              </div>

              {renderContent()}
            </div>
          </SidebarInset>
        </div>
        
        <BottomNavigation
          userType="breeder"
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          subscriptionActive={breeder.subscriptionActive}
        />
      </SidebarProvider>
    </ProtectedRoute>
  );
};

export default BreederDashboard;
